import { useState, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { useAudio } from "../hooks/useAudio";
import { STREAMS, FOLDER_TO_STREAM } from "../constants";
import { formatDuration, isAdvert } from "../utils";

export function PlayerBar() {
  const { state, togglePlay, setVolume, toggleMute, nowPlaying, audioRef } = useAudio();
  const stream = STREAMS[state.currentStream];
  const [artworkImages, setArtworkImages] = useState<string[]>([stream.artwork]);
  const [currentArtIndex, setCurrentArtIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const nowPlayingIsAd = isAdvert(nowPlaying?.albumFolder);

  useEffect(() => {
    if (nowPlayingIsAd && nowPlaying?.adImages && nowPlaying.adImages.length > 0) {
      setArtworkImages(nowPlaying.adImages);
      setCurrentArtIndex(0);
    } else if (nowPlayingIsAd) {
      setArtworkImages([stream.artwork]);
    } else if (nowPlaying?.albumFolder) {
      const albumArt = `/albums/${FOLDER_TO_STREAM[nowPlaying.albumFolder] || state.currentStream}.jpg`;
      setArtworkImages([albumArt]);
    } else {
      setArtworkImages([stream.artwork]);
    }
  }, [nowPlaying?.albumFolder, nowPlaying?.adImages, nowPlayingIsAd, state.currentStream, stream.artwork]);

  useEffect(() => {
    if (artworkImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentArtIndex((prev) => (prev + 1) % artworkImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [artworkImages.length]);

  useEffect(() => {
    if (!nowPlaying?.timestamp || !state.isPlaying) {
      return;
    }
    const calcElapsed = () => Math.floor(Date.now() / 1000 - nowPlaying.timestamp);
    setElapsed(calcElapsed());

    const interval = setInterval(() => {
      setElapsed(calcElapsed());
    }, 1000);
    return () => clearInterval(interval);
  }, [nowPlaying?.timestamp, state.isPlaying]);

  useEffect(() => {
    setElapsed(0);
  }, [nowPlaying?.title, nowPlaying?.timestamp]);

  const duration = nowPlaying?.duration || 0;
  const progress = duration > 0 ? Math.min((elapsed / duration) * 100, 100) : 0;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-[var(--bg-surface)]/95 backdrop-blur-xl border-t border-[var(--border-subtle)] z-50">
      {duration > 0 && !nowPlayingIsAd && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--border-subtle)]">
          <div
            className="h-full transition-all duration-1000 ease-linear"
            style={{
              width: `${progress}%`,
              backgroundColor: stream.color,
            }}
          />
        </div>
      )}

      <div className="flex items-center gap-4 max-w-[1600px] mx-auto px-6 md:px-8 lg:px-12 py-3">
        <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0 relative">
          {artworkImages.map((src, i) => (
            <img
              key={src}
              src={src}
              alt="Now playing"
              loading="eager"
              decoding="sync"
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{
                opacity: i === currentArtIndex ? 1 : 0.01,
                transform: 'translateZ(0)',
                WebkitTransform: 'translateZ(0)',
              }}
            />
          ))}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {state.isPlaying && (
              <span
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: stream.color }}
              />
            )}
            {nowPlayingIsAd && (
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 flex-shrink-0">
                AD
              </span>
            )}
            <span className="font-display text-lg tracking-wide truncate">
              {nowPlayingIsAd
                ? (nowPlaying?.title || "Ad Break")
                : (nowPlaying?.title
                  ? nowPlaying.title.replace(/^\['|'\]$/g, "")
                  : stream.name)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-[var(--text-secondary)] truncate">
              {nowPlayingIsAd
                ? (nowPlaying?.artist || stream.name)
                : (nowPlaying?.artist
                  ? nowPlaying.artist.replace(/^\['|'\]$/g, "")
                  : stream.description)}
            </span>
            {duration > 0 && !nowPlayingIsAd && (
              <span className="font-mono text-xs text-[var(--text-muted)]">
                {formatDuration(elapsed)} / {formatDuration(duration)}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={togglePlay}
            className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all hover:scale-105"
            style={{
              backgroundColor: stream.color,
              boxShadow: state.isPlaying ? `0 0 30px ${stream.color}50` : "none",
            }}
          >
            {state.isPlaying ? (
              <Pause size={22} fill="white" />
            ) : (
              <Play size={22} fill="white" className="ml-1" />
            )}
          </button>

          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={toggleMute}
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              {state.isMuted || state.volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={state.isMuted ? 0 : state.volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-24 h-1 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, ${stream.color} 0%, ${stream.color} ${
                  (state.isMuted ? 0 : state.volume) * 100
                }%, var(--border-muted) ${
                  (state.isMuted ? 0 : state.volume) * 100
                }%, var(--border-muted) 100%)`,
              }}
            />
          </div>

          {state.currentStream === "jukebox" && (
            <div className="hidden md:flex items-center gap-2 ml-2 pl-2 border-l border-[var(--border-subtle)]">
              <button
                onClick={async () => {
                  await fetch("/api/jukebox/force", { method: "POST" });
                  // Reconnect stream to flush browser audio buffer so new track plays immediately
                  if (audioRef.current && !audioRef.current.paused) {
                    const src = audioRef.current.src;
                    audioRef.current.src = "";
                    audioRef.current.src = src;
                    audioRef.current.play().catch(() => {});
                  }
                  window.dispatchEvent(new CustomEvent("jukebox-refresh"));
                  window.dispatchEvent(new CustomEvent("nowplaying-refresh"));
                }}
                className="px-3 py-1.5 text-xs font-mono rounded bg-[var(--bg-elevated)] hover:bg-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                title="Skip current track and play next from queue"
              >
                SKIP
              </button>
              <button
                onClick={async () => {
                  await fetch("/api/jukebox/flush", { method: "POST" });
                  window.dispatchEvent(new CustomEvent("jukebox-refresh"));
                }}
                className="px-3 py-1.5 text-xs font-mono rounded bg-[var(--bg-elevated)] hover:bg-red-500/20 text-[var(--text-secondary)] hover:text-red-400 transition-colors"
                title="Clear all tracks from queue"
              >
                FLUSH
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
